import { handleAjaxError } from "../actions/handleAjaxError";
import { syncContentGeneratorPoseThumbnails } from "@growtype-chat/partials/components/contentGeneratorPoseThumbnails";

const SPICY_MODE_REFRESH_PARAM = 'tm_spicy_refresh';

const invalidatePrivateNavigationCache = () => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const channel = new MessageChannel();
        const timeout = window.setTimeout(() => resolve(false), 500);
        channel.port1.onmessage = () => {
            window.clearTimeout(timeout);
            // Any reply proves that the active worker understands the
            // invalidation protocol. The cache entry may already be absent.
            resolve(true);
        };
        navigator.serviceWorker.controller.postMessage({
            type: 'talkiemate-private-navigation-invalidate',
            path: window.location.pathname,
        }, [channel.port2]);
    });
};

const reloadAfterSpicyModeChange = () => invalidatePrivateNavigationCache()
    .catch(() => false)
    .finally(() => {
        // A newly deployed worker may not control an already-open tab yet.
        // The one-time query forces this navigation around every HTML cache
        // layer. The next document removes it only after a worker acknowledges
        // the canonical-path invalidation below.
        const destination = new URL(window.location.href);
        destination.searchParams.set(SPICY_MODE_REFRESH_PARAM, Date.now().toString());
        window.location.replace(destination.toString());
    });

const clearSpicyModeRefreshMarker = () => {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(SPICY_MODE_REFRESH_PARAM)) {
        return;
    }

    invalidatePrivateNavigationCache()
        .then((acknowledged) => {
            if (!acknowledged) {
                return;
            }

            currentUrl.searchParams.delete(SPICY_MODE_REFRESH_PARAM);
            window.history.replaceState(
                window.history.state,
                '',
                `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
            );
        })
        .catch(() => {});
};

function enableNsfwMode() {
    clearSpicyModeRefreshMarker();
    const selector = 'input[name="nsfw_chat"], input[name="spicy_mode_is_active"]';

    const feedbackFor = (input) => {
        let feedback = $('.spicy-mode-feedback').first();
        if (feedback.length) {
            return feedback;
        }

        feedback = $('<p class="spicy-mode-feedback" role="status" aria-live="polite"></p>');
        const anchor = input.closest('.btn-toggle, label').first();
        (anchor.length ? anchor : input).after(feedback);
        return feedback;
    };

    const showFeedback = (input, message, includeUpgrade = false) => {
        const feedback = feedbackFor(input).empty().text(message);
        if (includeUpgrade) {
            feedback.append(' ').append(
                $('<a></a>')
                    .attr('href', '/plans/')
                    .text('View Premium plans')
            );
        }
    };

    let subscriptionModalRequest = null;

    const initializeSubscriptionSlider = (modalElement, attempt = 0) => {
        window.setTimeout(() => {
            const $slider = $(modalElement).find('.gwc-benefits-slider');
            if (!$slider.length) {
                return;
            }

            if (typeof $.fn.slick !== 'function') {
                if (attempt < 10) {
                    initializeSubscriptionSlider(modalElement, attempt + 1);
                }
                return;
            }

            if ($slider.hasClass('slick-initialized')) {
                $slider.slick('setPosition');
                return;
            }

            try {
                const sliderSettings = JSON.parse($slider.attr('data-gslick') || '{}');
                $slider.slick(sliderSettings);
            } catch (error) {
                console.error('Failed to initialize subscription slider:', error.message);
            }
        }, attempt === 0 ? 200 : 100);
    };

    const displaySubscriptionModal = (modalElement) => {
        if (!modalElement) {
            return false;
        }

        $('.spicy-mode-feedback').remove();

        if (window.growtypeModal && typeof window.growtypeModal.show === 'function') {
            window.growtypeModal.show(modalElement);
        } else {
            $(modalElement).modal('show');
        }

        initializeSubscriptionSlider(modalElement);

        return true;
    };

    const showSubscriptionModal = () => {
        const existingModal = document.getElementById('growtypeWcSubscriptionModal');
        if (existingModal) {
            return displaySubscriptionModal(existingModal);
        }

        if (!subscriptionModalRequest) {
            subscriptionModalRequest = jQuery.ajax({
                url: growtype_child_ajax.url,
                type: 'post',
                data: {
                    action: 'growtype_wc_get_subscription_modal',
                },
            }).then((response) => {
                if (!response?.success || !response?.data?.html) {
                    throw new Error('Subscription modal markup was not returned.');
                }

                const template = document.createElement('template');
                template.innerHTML = response.data.html.trim();
                const modalElement = template.content.querySelector('#growtypeWcSubscriptionModal');

                if (!modalElement) {
                    throw new Error('Subscription modal markup is invalid.');
                }

                document.body.appendChild(modalElement);
                return modalElement;
            }).always(() => {
                subscriptionModalRequest = null;
            });
        }

        subscriptionModalRequest
            .then((modalElement) => displaySubscriptionModal(modalElement))
            .catch((response) => handleAjaxError(response));

        return true;
    };

    const restoreState = (input, previousState) => {
        $(selector)
            .prop('checked', previousState)
            .data('persistedSpicyState', previousState);
        syncContentGeneratorPoseThumbnails($(document), previousState);
        input.attr('aria-checked', previousState ? 'true' : 'false');
    };

    $(selector).each(function () {
        const input = $(this);
        input
            .data('persistedSpicyState', input.is(':checked'))
            .attr('aria-checked', input.is(':checked') ? 'true' : 'false');
    });

    $(document)
        .off('click.spicyPreferenceToggle', '.btn-toggle')
        .on('click.spicyPreferenceToggle', '.btn-toggle', function (event) {
            const wrapper = $(this);
            const input = wrapper.find(selector).first();

            if (!input.length || $(event.target).is(selector)) {
                return;
            }

            event.preventDefault();

            if (input.prop('disabled')) {
                return;
            }

            input.prop('checked', !input.is(':checked')).trigger('change');
        });

    $(document)
        .off('change.spicyPreference', selector)
        .on('change.spicyPreference', selector, function () {
            const input = $(this);
            const isActive = input.is(':checked');
            const storedState = input.data('persistedSpicyState');
            const previousState = typeof storedState === 'boolean'
                ? storedState
                : !isActive;
            const shouldReload = input.attr('name') === 'spicy_mode_is_active';

            input.prop('disabled', true).attr('aria-busy', 'true');
            $('.spicy-mode-feedback').remove();
            syncContentGeneratorPoseThumbnails($(document), isActive);

            jQuery.ajax({
                url: growtype_child_ajax.url,
                type: 'post',
                data: {
                    action: 'growtype_child_enable_spicy_mode',
                    enabled: isActive,
                },
                success: function (response) {
                    if (!response.success) {
                        restoreState(input, previousState);
                        if (response?.data?.message === 'profile_required') {
                            $('.spicy-mode-feedback').remove();
                            $('#growtypeFormAuthModal').modal('show');
                        } else {
                            if (!showSubscriptionModal()) {
                                handleAjaxError({ responseJSON: response });
                            }
                        }
                        return;
                    }

                    $(selector)
                        .prop('checked', isActive)
                        .data('persistedSpicyState', isActive)
                        .attr('aria-checked', isActive ? 'true' : 'false');
                    syncContentGeneratorPoseThumbnails($(document), isActive);

                    if (shouldReload) {
                        reloadAfterSpicyModeChange();
                    }
                },
                error: function (response) {
                    restoreState(input, previousState);
                    if (response?.responseJSON?.data?.message === 'profile_required') {
                        $('.spicy-mode-feedback').remove();
                        $('#growtypeFormAuthModal').modal('show');
                    } else if (Number(response?.status) === 403) {
                        if (!showSubscriptionModal()) {
                            handleAjaxError(response);
                        }
                    } else {
                        showFeedback(input, 'Spicy mode could not be changed. Please try again.');
                        handleAjaxError(response);
                    }
                },
                complete: function () {
                    input.prop('disabled', false).removeAttr('aria-busy');
                }
            });
        });
}

export { enableNsfwMode };
